// Tracks the org the superadmin is currently operating inside. Only matters
// for users with `org:manage_all`; for everyone else the store stays empty
// and the API interceptor never sends `X-Org-Id`.
//
// Persisted in localStorage so the choice survives a refresh. Changing the
// active org triggers a hard reload so every loaded view re-fetches under
// the new tenant — simpler and safer than invalidating each query's cache.
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { setActiveOrgID, getActiveOrgID } from "@/services/api";
import { useAuthStore } from "@/stores/auth";

const STORAGE_KEY = "luckydesk.activeOrg";

export interface ActiveOrgEntry {
  id: string;
  slug: string;
  name: string;
}

export const useActiveOrgStore = defineStore("active-org", () => {
  const orgs = ref<ActiveOrgEntry[]>([]);
  const activeID = ref<string | null>(null);

  const auth = useAuthStore();

  const canSwitch = computed(() => auth.hasPermission("org:manage_all"));

  const activeOrg = computed<ActiveOrgEntry | null>(() => {
    if (!activeID.value) return null;
    return orgs.value.find((o) => o.id === activeID.value) ?? null;
  });

  function setOrgs(list: ActiveOrgEntry[]) {
    orgs.value = list;
  }

  // hydrate restores the active org from localStorage on app boot. We don't
  // try to validate it against the org list here — the caller should call
  // setOrgs() shortly after, and a stale id simply means the dropdown shows
  // the home org until the user picks a valid one.
  function hydrate() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      activeID.value = stored;
      setActiveOrgID(stored === auth.user?.org_id ? null : stored);
    }
  }

  function setActive(id: string | null, opts: { reload?: boolean } = { reload: true }) {
    if (!canSwitch.value) return;
    activeID.value = id;
    if (id && id !== auth.user?.org_id) {
      localStorage.setItem(STORAGE_KEY, id);
      setActiveOrgID(id);
    } else {
      // Back to "my home org" — clear the header so the JWT's own org_id wins.
      localStorage.removeItem(STORAGE_KEY);
      setActiveOrgID(null);
    }
    if (opts.reload) {
      // Hard reload — pages re-fetch under the new tenant. Simpler than
      // hunting every cached view.
      window.location.reload();
    }
  }

  function reset() {
    orgs.value = [];
    activeID.value = null;
    localStorage.removeItem(STORAGE_KEY);
    setActiveOrgID(null);
  }

  // Convenience for tests / SSR scenarios where we need the raw header.
  function headerValue(): string | null {
    return getActiveOrgID();
  }

  return {
    orgs,
    activeID,
    activeOrg,
    canSwitch,
    setOrgs,
    hydrate,
    setActive,
    reset,
    headerValue,
  };
});
